import DateObject from 'react-date-object'
import persian from 'react-date-object/calendars/persian'
import {parsePersianInvoiceDate} from './invoiceDateDeadline'
import {getTehranWallClockDate, TEHRAN_TIME_ZONE} from './tehranTime'

type InvoiceArticle9Request = {
  isArticle9?: true
  indati2m?: number
}

const getTimeZoneParts = (unixMilliseconds: number) => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: TEHRAN_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(unixMilliseconds))
  const number = (type: string) => Number(parts.find((part) => part.type === type)?.value)

  return {
    year: number('year'),
    month: number('month'),
    day: number('day'),
    hour: number('hour'),
    minute: number('minute'),
    second: number('second'),
  }
}

const tehranWallClockToUnixMilliseconds = (wallClock: Date) => {
  const wallClockAsUtc = Date.UTC(
    wallClock.getFullYear(),
    wallClock.getMonth(),
    wallClock.getDate(),
    wallClock.getHours(),
    wallClock.getMinutes(),
    wallClock.getSeconds()
  )
  let instant = wallClockAsUtc

  // Two passes also cover historical offset transitions without depending on the browser time zone.
  for (let attempt = 0; attempt < 2; attempt += 1) {
    const zoned = getTimeZoneParts(instant)
    const offset =
      Date.UTC(zoned.year, zoned.month - 1, zoned.day, zoned.hour, zoned.minute, zoned.second) -
      instant
    instant = wallClockAsUtc - offset
  }

  return instant
}

const article9DateTimeToUnixMilliseconds = (value?: string | null) => {
  const wallClock = parsePersianInvoiceDate(value)
  if (!wallClock) return undefined

  const unixMilliseconds = tehranWallClockToUnixMilliseconds(wallClock)
  return Number.isSafeInteger(unixMilliseconds) && `${Math.abs(unixMilliseconds)}`.length <= 13
    ? unixMilliseconds
    : undefined
}

const article9UnixMillisecondsToDateTime = (value?: number | null) => {
  if (!Number.isSafeInteger(value) || value == null) return ''

  const instant = new Date(value)
  if (Number.isNaN(instant.getTime())) return ''

  return new DateObject({date: getTehranWallClockDate(instant), calendar: persian}).format(
    'YYYY/MM/DD HH:mm'
  )
}

const getInvoiceArticle9DraftError = (
  isArticle9: boolean,
  invoiceType: number,
  registrationDateTime: string,
  issueDateTime?: string,
  now?: string
) => {
  if (!isArticle9) return undefined
  if (invoiceType === 3) return 'صورتحساب نوع سوم نمی‌تواند موضوع ماده ۹ باشد.'

  const registrationUnixMilliseconds = article9DateTimeToUnixMilliseconds(registrationDateTime)
  if (registrationUnixMilliseconds == null) {
    return 'تاریخ و زمان ثبت صورتحساب موضوع ماده ۹ را وارد کنید.'
  }

  const issueUnixMilliseconds = article9DateTimeToUnixMilliseconds(issueDateTime)
  if (issueUnixMilliseconds != null && registrationUnixMilliseconds < issueUnixMilliseconds) {
    return 'تاریخ و زمان ثبت صورتحساب نمی‌تواند پیش از تاریخ صدور باشد.'
  }

  const nowUnixMilliseconds = article9DateTimeToUnixMilliseconds(now)
  if (nowUnixMilliseconds != null && registrationUnixMilliseconds > nowUnixMilliseconds) {
    return 'تاریخ و زمان ثبت صورتحساب نمی‌تواند در آینده باشد.'
  }

  return undefined
}

const buildInvoiceArticle9Request = (
  isArticle9: boolean,
  invoiceType: number,
  registrationDateTime: string
): InvoiceArticle9Request => {
  if (!isArticle9 || invoiceType === 3) {
    return {isArticle9: undefined, indati2m: undefined}
  }

  return {
    isArticle9: true,
    indati2m: article9DateTimeToUnixMilliseconds(registrationDateTime),
  }
}

export {
  article9DateTimeToUnixMilliseconds,
  article9UnixMillisecondsToDateTime,
  buildInvoiceArticle9Request,
  getInvoiceArticle9DraftError,
}
export type {InvoiceArticle9Request}
